import { useState } from 'react';
import {
  AccountModel,
  BoPhan,
  Color,
  DonViTinh,
  Group,
  KhoHang,
  KichCo,
  LoaiVatTu,
  Phi,
  QuocGia,
  Tax,
  ThuChi,
  VatTuSanPham
} from '@/types/schemas';
import { ViTriKho } from '@/types/schemas/vi-tri-kho.type';

export interface FormFieldState {
  // AccountTab states
  tkKho: AccountModel | null | undefined;
  tkDoanhThu: AccountModel | null | undefined;
  tkGiaVon: AccountModel | null | undefined;
  tkChietKhau: AccountModel | null | undefined;
  tkKhuyenMai: AccountModel | null | undefined;
  tkTraLai: AccountModel | null | undefined;
  tkSPDD: AccountModel | null | undefined;
  tkCPNVL: AccountModel | null | undefined;

  // AttributeTab states
  nuocSX: QuocGia | null;
  mauSac: Color | null;
  kichCo: KichCo | null;

  // GeneralTab states
  donViTinh: DonViTinh | null;
  donViTinhNhapLieu: DonViTinh | null;
  nhom1: Group | null;
  nhom2: Group | null;
  nhom3: Group | null;
  khoMacDinh: KhoHang | null;
  viTri: ViTriKho | null;
  thueMacDinh: Tax | null;
  thueNhapKhau: Tax | null;
  loaiVatTu: LoaiVatTu | null;
  phi: Phi | null;
  thuChi: ThuChi | null;

  // OtherTab states
  boPhan: BoPhan | null;
}

export interface FormFieldActions {
  // AccountTab handlers
  setTkKho: (row: AccountModel | null | undefined) => void;
  setTkDoanhThu: (row: AccountModel | null | undefined) => void;
  setTkGiaVon: (row: AccountModel | null | undefined) => void;
  setTkChietKhau: (row: AccountModel | null | undefined) => void;
  setTkKhuyenMai: (row: AccountModel | null | undefined) => void;
  setTkTraLai: (row: AccountModel | null | undefined) => void;
  setTkSPDD: (row: AccountModel | null | undefined) => void;
  setTkCPNVL: (row: AccountModel | null | undefined) => void;

  // AttributeTab handlers
  setnuocSX: (nuocSX: QuocGia) => void;
  setmauSac: (mauSac: Color) => void;
  setkichCo: (kichCo: KichCo) => void;

  // GeneralTab handlers
  setdonViTinh: (donViTinh: DonViTinh) => void;
  setdonViTinhNhapLieu: (donViTinhNhapLieu: DonViTinh) => void;
  setnhom1: (nhom1: Group) => void;
  setnhom2: (nhom2: Group) => void;
  setnhom3: (nhom3: Group) => void;
  setkhoMacDinh: (khoMacDinh: KhoHang) => void;
  setviTri: (viTriMacDinh: ViTriKho) => void;
  setthueMacDinh: (thueMacDinh: Tax) => void;
  setthueNhapKhau: (thueNhapKhau: Tax) => void;
  setloaiVatTu: (loaiVatTu: LoaiVatTu) => void;
  setphi: (phi: Phi) => void;
  setthuChi: (thuChi: ThuChi) => void;

  // OtherTab handlers
  setboPhan: (boPhan: BoPhan) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
  mergeData: () => Record<string, string | null>;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  // AccountTab states
  tkKho: null,
  tkDoanhThu: null,
  tkGiaVon: null,
  tkChietKhau: null,
  tkKhuyenMai: null,
  tkTraLai: null,
  tkSPDD: null,
  tkCPNVL: null,

  // AttributeTab states
  nuocSX: null,
  mauSac: null,
  kichCo: null,

  // GeneralTab states
  donViTinh: null,
  donViTinhNhapLieu: null,
  nhom1: null,
  nhom2: null,
  nhom3: null,
  khoMacDinh: null,
  viTri: null,
  thueMacDinh: null,
  thueNhapKhau: null,
  loaiVatTu: null,
  phi: null,
  thuChi: null,

  // OtherTab states
  boPhan: null
};

function transformInitialData(initialData?: VatTuSanPham): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    // AccountTab states
    tkKho: initialData.tk_khcp_data || null,
    tkDoanhThu: initialData.tk_dt_data || null,
    tkGiaVon: initialData.tk_gv_data || null,
    tkChietKhau: initialData.tk_ck_data || null,
    tkKhuyenMai: initialData.tk_km_data || null,
    tkTraLai: initialData.tk_tl_data || null,
    tkSPDD: initialData.tk_spdd_data || null,
    tkCPNVL: initialData.tk_cpnvl_data || null,

    // AttributeTab states
    nuocSX: initialData.nuoc_sx_data || null,
    mauSac: initialData.mau_sac_data || null,
    kichCo: initialData.kich_co_data || null,

    // GeneralTab states
    donViTinh: initialData.dvt_data || null,
    donViTinhNhapLieu: initialData.dvt_nl_data || null,
    nhom1: initialData.nh_vt1_data || null,
    nhom2: initialData.nh_vt2_data || null,
    nhom3: initialData.nh_vt3_data || null,
    khoMacDinh: initialData.ma_kho_data || null,
    viTri: initialData.ma_vi_tri_data || null,
    thueMacDinh: initialData.ma_thue_data || null,
    thueNhapKhau: initialData.ma_thue_nk_data || null,
    loaiVatTu: initialData.ma_lvt_data || null,
    phi: initialData.ma_phi_data || null,
    thuChi: initialData.ma_thu_chi_data || null,

    // OtherTab states
    boPhan: initialData.ma_bp_data || null
  };
}

export function useFormFieldState(initialData?: VatTuSanPham): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    // AccountTab handlers
    setTkKho: (tkKho: AccountModel | null | undefined) => {
      setState(prev => ({
        ...prev,
        tkKho
      }));
    },

    setTkDoanhThu: (tkDoanhThu: AccountModel | null | undefined) => {
      setState(prev => ({
        ...prev,
        tkDoanhThu
      }));
    },

    setTkGiaVon: (tkGiaVon: AccountModel | null | undefined) => {
      setState(prev => ({
        ...prev,
        tkGiaVon
      }));
    },

    setTkChietKhau: (tkChietKhau: AccountModel | null | undefined) => {
      setState(prev => ({
        ...prev,
        tkChietKhau
      }));
    },

    setTkKhuyenMai: (tkKhuyenMai: AccountModel | null | undefined) => {
      setState(prev => ({
        ...prev,
        tkKhuyenMai
      }));
    },

    setTkTraLai: (tkTraLai: AccountModel | null | undefined) => {
      setState(prev => ({
        ...prev,
        tkTraLai
      }));
    },

    setTkSPDD: (tkSPDD: AccountModel | null | undefined) => {
      setState(prev => ({
        ...prev,
        tkSPDD
      }));
    },

    setTkCPNVL: (tkCPNVL: AccountModel | null | undefined) => {
      setState(prev => ({
        ...prev,
        tkCPNVL
      }));
    },

    // AttributeTab handlers
    setnuocSX: (nuocSX: QuocGia) => {
      setState(prev => ({ ...prev, nuocSX: nuocSX }));
    },

    setmauSac: (mauSac: Color) => {
      setState(prev => ({ ...prev, mauSac: mauSac }));
    },

    setkichCo: (kichCo: KichCo) => {
      setState(prev => ({ ...prev, kichCo: kichCo }));
    },

    // GeneralTab handlers
    setdonViTinh: (donViTinh: DonViTinh) => {
      setState(prev => ({ ...prev, donViTinh: donViTinh }));
    },

    setdonViTinhNhapLieu: (donViTinhNhapLieu: DonViTinh) => {
      setState(prev => ({ ...prev, donViTinhNhapLieu: donViTinhNhapLieu }));
    },

    setnhom1: (nhom1: Group) => {
      setState(prev => ({ ...prev, nhom1: nhom1 }));
    },

    setnhom2: (nhom2: Group) => {
      setState(prev => ({ ...prev, nhom2: nhom2 }));
    },

    setnhom3: (nhom3: Group) => {
      setState(prev => ({ ...prev, nhom3: nhom3 }));
    },

    setkhoMacDinh: (khoMacDinh: KhoHang) => {
      setState(prev => ({ ...prev, khoMacDinh: khoMacDinh }));
    },

    setviTri: (viTriMacDinh: ViTriKho) => {
      setState(prev => ({ ...prev, viTri: viTriMacDinh }));
    },

    setthueMacDinh: (thueMacDinh: Tax) => {
      setState(prev => ({ ...prev, thueMacDinh: thueMacDinh }));
    },

    setthueNhapKhau: (thueNhapKhau: Tax) => {
      setState(prev => ({ ...prev, thueNhapKhau: thueNhapKhau }));
    },

    // OtherTab handlers
    setloaiVatTu: (loaiVatTu: LoaiVatTu) => {
      setState(prev => ({ ...prev, loaiVatTu: loaiVatTu }));
    },
    setboPhan: (boPhan: BoPhan) => {
      setState(prev => ({ ...prev, boPhan: boPhan }));
    },
    setphi: (phi: Phi) => {
      setState(prev => ({ ...prev, phi: phi }));
    },
    setthuChi: (thuChi: ThuChi) => {
      setState(prev => ({ ...prev, thuChi: thuChi }));
    },

    // Utility functions
    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    },

    mergeData: () => {
      return {
        // AccountTab uuids
        tk_khcp: state.tkKho?.uuid || null,
        tk_dt: state.tkDoanhThu?.uuid || null,
        tk_gv: state.tkGiaVon?.uuid || null,
        tk_ck: state.tkChietKhau?.uuid || null,
        tk_km: state.tkKhuyenMai?.uuid || null,
        tk_tl: state.tkTraLai?.uuid || null,
        tk_spdd: state.tkSPDD?.uuid || null,
        tk_cpnvl: state.tkCPNVL?.uuid || null,

        // AttributeTab uuids
        nuoc_sx: state.nuocSX?.uuid || null,
        mau_sac: state.mauSac?.uuid || null,
        kich_co: state.kichCo?.uuid || null,

        // GeneralTab uuids
        dvt: state.donViTinh?.uuid || null,
        dvt_nl: state.donViTinhNhapLieu?.uuid || null,
        nh_vt1: state.nhom1?.uuid || null,
        nh_vt2: state.nhom2?.uuid || null,
        nh_vt3: state.nhom3?.uuid || null,
        ma_kho: state.khoMacDinh?.uuid || null,
        ma_vi_tri: state.viTri?.uuid || null,
        ma_thue: state.thueMacDinh?.uuid || null,
        ma_lvt: state.loaiVatTu?.uuid || null,
        ma_phi: state.phi?.uuid || null,
        ma_thu_chi: state.thuChi?.uuid || null,

        // OtherTab uuids
        ma_bp: state.boPhan?.uuid || null
      };
    }
  };

  return { state, actions };
}
